import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  contentChild,
  DestroyRef,
  inject,
  input,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';

import { AnyEntity, ButtonModule, DropdownModule, IconModule, SearchInputModule, SimpleInputModule, StrNum } from '@hglobal/common';
import { FormControl } from '@ngneat/reactive-forms';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, tap } from 'rxjs';

@Component({
  imports: [
    NgTemplateOutlet,
    DropdownModule,
    SimpleInputModule,
    SearchInputModule,
    IconModule,
    TranslateModule,
    AsyncPipe,
    ReactiveFormsModule,
    ButtonModule,
  ],
  selector: 'pp-search-select',
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchSelectComponent implements OnInit, OnChanges {
  readonly #destroyRef = inject(DestroyRef);
  #value: StrNum[] = [];

  readonly control = input<FormControl<StrNum>>();
  readonly items = input<AnyEntity[]>();
  readonly placeholder = input<string>();
  readonly label = input<string>();
  readonly hint = input<string>();
  readonly nameField = input('name');
  readonly valueField = input('id');
  readonly height = input(44);
  @Input() searchByFields: string[] = [];
  readonly required = input(false, { transform: booleanAttribute });
  readonly multi = input(false, { transform: booleanAttribute });
  readonly clearable = input(false, { transform: booleanAttribute });

  readonly itemTpl = contentChild<TemplateRef<any>>('itemTpl');

  searchControl = new FormControl<string>('');
  filteredItems = signal<AnyEntity[]>([]);

  get selectedValue(): AnyEntity | string {
    const selectedItems = this.#value.map(val => this.items().find(item => item[this.valueField()] === val));

    return this.multi() ? selectedItems.map(item => item[this.nameField()]).join(', ') : selectedItems[0];
  }

  ngOnInit(): void {
    const control = this.control();
    this.#setInitialValue(control.value);
    this.searchByFields = [this.nameField(), ...this.searchByFields];

    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.#destroyRef))
      .subscribe(searchTerm => this.filterItems(searchTerm));

    control.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap(value => this.#setInitialValue(value)),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.filteredItems.set(this.items());
      this.searchControl.setValue('');
    }
  }

  filterItems(search: string): void {
    if (!search) {
      this.filteredItems.set(this.items());
      return;
    }

    const filtered = this.items().filter(item =>
      this.searchByFields.some(field => item[field]?.toString().toLowerCase().includes(search.toLowerCase())),
    );
    this.filteredItems.set(filtered);
  }

  toggleItem(item: AnyEntity, event: Event) {
    const multi = this.multi();
    if (multi) {
      event.stopPropagation();
    }

    const itemValue = item[this.valueField()];

    const value = multi ? (this.isSelected(item) ? this.#value.filter(val => val !== itemValue) : [...this.#value, itemValue]) : itemValue;

    this.control().setValue(multi ? value.join(',') : value);
  }

  isSelected(item: AnyEntity): boolean {
    return this.#value.includes(item[this.valueField()]);
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();
    this.control().setValue(this.multi() ? '' : null);
  }

  #setInitialValue(value: StrNum): void {
    if (!value) {
      this.#value = [];
      this.searchControl.reset();
      return;
    }

    if (this.multi() && typeof value === 'string') {
      this.#value = value.split(',').map(Number);
      return;
    }

    this.#value = [value];
  }
}
